// src/components/shell/MobileSidebar.tsx
// Phase 6.7.1 - Mobile navigation drawer.
//
// Shown below the lg breakpoint as a left-side Sheet. The trigger lives in
// Header.tsx as a hamburger button; this component owns the drawer body and
// the close-on-navigation behavior.
//
// Reuses the same nav items hook as the desktop Sidebar so adding a future
// route only requires editing useNavItems.ts. The Brain trigger is NOT
// duplicated here because it already lives in the Header on mobile.

import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Settings } from "lucide-react";
import { Wordmark } from "@/components/brand/Logo";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useNavItems, type NavItem } from "./useNavItems";
import { cn } from "@/lib/utils";

interface MobileSidebarProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MobileSidebar({ open, onOpenChange }: MobileSidebarProps) {
  const { t } = useTranslation();
  const items = useNavItems();

  // Close the drawer whenever the user navigates. The Sheet does not know
  // about route changes; we trigger close from each link's onClick.
  function handleNavClick() {
    onOpenChange(false);
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="left"
        className="flex flex-col p-0 gap-0 w-[280px] bg-sidebar text-sidebar-foreground border-r border-sidebar-border"
      >
        <SheetHeader className="px-6 h-16 flex flex-row items-center border-b border-sidebar-border space-y-0">
          <SheetTitle asChild>
            <div>
              <Wordmark className="text-sidebar-foreground" />
            </div>
          </SheetTitle>
        </SheetHeader>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5 scrollbar-thin">
          {items.map((item) => (
            <MobileNavLink key={item.to} {...item} onClick={handleNavClick} />
          ))}
        </nav>

        <div className="px-3 py-3 border-t border-sidebar-border">
          <MobileNavLink
            to="/app/settings/organization"
            label={t("nav.settings")}
            icon={Settings}
            onClick={handleNavClick}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}

function MobileNavLink({
  to,
  label,
  icon: Icon,
  onClick,
}: NavItem & { onClick: () => void }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
          "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
          "min-h-[44px]",
          isActive
            ? "bg-sidebar-accent text-sidebar-accent-foreground"
            : "text-sidebar-foreground/80",
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="truncate">{label}</span>
    </NavLink>
  );
}